import type { MdfFieldInitializationRule } from '../dto/mdfInitializationRuleTypes';
import type { FieldValue } from '../dto/fieldValue';

/**
 * The current supported MDF Field types.
 */
export enum FieldType {
  Text = 'text',
  Checkbox = 'checkbox',
  Date = 'date',
  Number = 'number',
  Choice = 'choice',
  MultipleChoice = 'multiplechoice',
  Link = 'link',
  User = 'user',
  File = 'file',
  Button = 'button',
  JSON = 'json',
  SubType = 'subtype',
  TreeChoice = 'treechoice'
}

export type AlternativeValue = string | number | boolean;

/**
 * This represents an Alternative in a Choice type.
 */
export interface Alternative {
  label: string;
  value: AlternativeValue;
}

export interface MdfFieldEntity {
  // An internal unique ID used to map a field in a payload to its
  // corresponding definition in the model
  id: string;

  // A user-defined unique id that is used when searching or presenting search results.
  // For instance, if we have a match for 'bar' in a value for a field whose fieldId is
  // 'foo', the search hit will be presented as 'foo:bar'
  fieldId: string;

  // The type of data of this field
  type: FieldType;

  // Is this field mandatory ?
  required: boolean;

  // The default value to apply
  defaultValue?: FieldValue;

  // Rules to initialize this field at ingest time
  initializationRules?: MdfFieldInitializationRule[];

  // Is this field a default system field ?
  systemDefaultField?: boolean;

  /*
    We are using alternative for two cases:
    1. If the field is a choice, these are the alternatives to pick from
    2. If the field is a subtype, the alternatives hold the references of subtype schemas to pick from
  */
  alternatives?: Alternative[];

  // References an option list to be used instead of an inline alternative
  // list. This field should only be used when editing the mdf definition.
  // Any other call to get an mdf should reply with the alternatives field
  // populated by a copy of the option list
  optionListId?: string;

  // Whether a field is sortable. Only allowed on Date fields in DEFAULT_MDF.
  sortable?: boolean;

  // Whether a field is forced read only - no user will be allowed to edit it via the UI. REST API ignores this value.
  readOnly?: boolean;

  // Whether a multiple choice field allows users to enter/remove their own values
  freeform?: boolean;

  // If set, editing workflows will take these values into account. FieldType.Text only.
  maxCharacters?: number | null;
  minCharacters?: number | null;

  userCanRead?: boolean;
  userCanWrite?: boolean;

  // File extensions that are allowed to upload on file type field.
  fileTypes?: string;

  // If true we show a dialog before sending client hook for field type button
  showDialogBeforeClientHook?: boolean;

  // The schema model of treeChoice field.
  treeAlternatives?: string[][];
}

export type FieldDefinition = MdfFieldEntity & { schemaId: string };
